import { getCurrentUser } from '@/services/api'
import { useWallet } from '@components/providers/WalletProvider'
import { LoginSheet } from '@components/ui/LoginSheet'
import { Onboarding } from '@components/ui/Onboarding'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export function PreLogin() {
  const navigate = useNavigate()
  const {
    isAuthenticated,
    isReady,
    isSmartWalletReady,
    isCognitoAuthenticated,
    isCreatingEmbeddedWallet,
    isCognitoAuthenticating,
    isPreparingSmartWallet,
    walletError,
    retrySmartWallet,
    logout,
  } = useWallet()
  const [isLoginOpen, setLoginOpen] = useState(false)
  const [showWalletCreationLoader, setShowWalletCreationLoader] = useState(false)
  const creationStartRef = useRef<number | null>(null)
  const hideLoaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasCheckedUserStatusRef = useRef(false)
  const [isCheckingUserStatus, setIsCheckingUserStatus] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !isReady) {
      hasCheckedUserStatusRef.current = false
      setIsCheckingUserStatus(false)
    }
  }, [isAuthenticated, isReady])

  useEffect(() => {
    if (
      !isReady ||
      !isAuthenticated ||
      !isSmartWalletReady ||
      !isCognitoAuthenticated ||
      hasCheckedUserStatusRef.current
    ) {
      return
    }

    hasCheckedUserStatusRef.current = true
    let cancelled = false

    const checkUserStatus = async () => {
      setIsCheckingUserStatus(true)
      try {
        const response = await getCurrentUser()
        if (cancelled) return
        if (response.user.isActive) {
          navigate('/home', { replace: true })
          return
        }
      } catch (error) {
        console.error('Failed to fetch user status:', error)
        if (cancelled) return
      } finally {
        if (!cancelled) {
          setIsCheckingUserStatus(false)
        }
      }

      if (!cancelled) {
        navigate('/signup-complete', { replace: true })
      }
    }

    void checkUserStatus()

    return () => {
      cancelled = true
    }
  }, [isReady, isAuthenticated, isSmartWalletReady, isCognitoAuthenticated, navigate])

  useEffect(() => {
    if (hideLoaderTimeoutRef.current) {
      clearTimeout(hideLoaderTimeoutRef.current)
      hideLoaderTimeoutRef.current = null
    }

    if (isCreatingEmbeddedWallet) {
      if (!creationStartRef.current) {
        creationStartRef.current = Date.now()
      }
      setShowWalletCreationLoader(true)
      return
    }

    if (!creationStartRef.current) {
      setShowWalletCreationLoader(false)
      return
    }

    const elapsed = Date.now() - creationStartRef.current
    const minimumDuration = 3000
    const remaining = Math.max(0, minimumDuration - elapsed)

    hideLoaderTimeoutRef.current = setTimeout(() => {
      setShowWalletCreationLoader(false)
      creationStartRef.current = null
      hideLoaderTimeoutRef.current = null
    }, remaining)

    return () => {
      if (hideLoaderTimeoutRef.current) {
        clearTimeout(hideLoaderTimeoutRef.current)
        hideLoaderTimeoutRef.current = null
      }
    }
  }, [isCreatingEmbeddedWallet])

  if (!isReady) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 w-full max-w-md mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-xl font-bold mb-2">ウォレット読み込み中...</p>
          </div>
        </div>
      </div>
    )
  }

  if (walletError) {
    return (
      <div className="min-h-screen w-full bg-white text-slate-900 flex flex-col items-center justify-center px-6 text-center">
        <img src="/icons/ARCSFeatureIcon.svg" alt="ARCS Feature" className="h-24 w-24" />
        <div className="mt-6 space-y-3 max-w-md" aria-live="assertive">
          <p className="text-lg font-semibold">ウォレットの準備に失敗しました</p>
          <p className="text-sm text-slate-500">{walletError}</p>
        </div>
        <div className="mt-8 flex flex-col gap-3 w-full max-w-xs">
          <button
            type="button"
            onClick={retrySmartWallet}
            disabled={isPreparingSmartWallet}
            className="w-full rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-blue-600"
          >
            {isPreparingSmartWallet ? '再試行中...' : '再試行'}
          </button>
          <button
            type="button"
            onClick={() => {
              void logout()
            }}
            className="w-full rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
          >
            ログインし直す
          </button>
        </div>
      </div>
    )
  }

  if (showWalletCreationLoader || isPreparingSmartWallet) {
    return (
      <div className="min-h-screen w-full bg-white text-slate-900 flex flex-col items-center justify-center px-6 text-center">
        <img src="/icons/ARCSFeatureIcon.svg" alt="ARCS Feature" className="h-24 w-24" />
        <div className="mt-6 space-y-3" aria-live="polite">
          <p className="text-lg font-semibold">ウォレットを作成しています...</p>
          <p className="text-sm text-slate-500">
            初回ログインに必要なウォレットを準備しています。画面が切り替わるまでそのままお待ちください。
          </p>
        </div>
      </div>
    )
  }

  if (isAuthenticated || isCognitoAuthenticating || isCheckingUserStatus) {
    return (
      <div className="min-h-screen w-full bg-white text-slate-900 flex flex-col items-center justify-center px-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        <div className="mt-6 space-y-3" aria-live="polite">
          <p className="text-lg font-semibold">アカウントを確認しています...</p>
          <p className="text-sm text-slate-500">
            アカウント状態を確認しています。ページが切り替わるまでそのままでお待ちください。
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen w-full bg-[#F5F5F5] text-foreground flex items-center justify-center">
      <div className="w-full max-w-[480px] bg-[#eff6ff] h-screen px-4 py-10 flex flex-col justify-end">
        <Onboarding login={() => setLoginOpen(true)} />
      </div>
      <LoginSheet open={isLoginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  )
}

export default PreLogin
